/**
 * delivery 一覧キャッシュ（無限スクロール）の部分更新。
 *
 * 表示上書き（vehicleDisplayOverrides）は左リストの行グループにしか効かないため、上書きを
 * 外した瞬間にサーバの値へ戻って見える。/map の再取得は 9〜16 秒かかるので、その間ずっと
 * 古い車番が出たままになる。更新が成功した時点でキャッシュ側も新しい値にして、
 * 再取得はそのあと裏で走らせる。
 */

type LegRunLike = { id?: number | null; truck_number?: string | null };
type DeliveryLike = {
  id: number;
  truckNumber?: string | null;
  shipmentOrderLegRuns?: LegRunLike[];
};
type PageLike<T extends DeliveryLike> = { data: T[] };
export type InfiniteDeliveriesCache<T extends DeliveryLike> = {
  pages: PageLike<T>[];
  pageParams: unknown[];
};

/**
 * 勤務 (shift_truck.id = delivery.id) の車番を書き換える。
 *
 * 配下の leg_run.truck_number も同じ値へ揃える。API 側も同じことをしており
 * (TruckNumbersController#sync_to_assigned_leg_runs)、車番の表示は
 * shift_truck → leg_run → truck の順に落ちるため、leg_run に古い値が残ると
 * 空にしたはずの車番が復活する。
 *
 * 対象がいないページ・キャッシュは同じ参照を返す（触るたびに全画面が再描画されるのを防ぐ）。
 */
export function patchDeliveryTruckNumber<T extends DeliveryLike, C extends InfiniteDeliveriesCache<T>>(
  cache: C | undefined,
  shiftTruckId: number,
  truckNumber: string,
): C | undefined {
  if (!cache?.pages) return cache;

  // API と同じく空文字は未設定 (nil) として扱う
  const nextValue = truckNumber.trim().length > 0 ? truckNumber : null;

  let changed = false;
  const pages = cache.pages.map((page) => {
    if (!page.data?.some((delivery) => delivery.id === shiftTruckId)) return page;
    changed = true;
    return {
      ...page,
      data: page.data.map((delivery) => {
        if (delivery.id !== shiftTruckId) return delivery;
        return {
          ...delivery,
          truckNumber: nextValue,
          ...(delivery.shipmentOrderLegRuns
            ? {
              shipmentOrderLegRuns: delivery.shipmentOrderLegRuns.map((legRun) => (
                { ...legRun, truck_number: nextValue }
              )),
            }
            : {}),
        };
      }),
    };
  });

  return changed ? { ...cache, pages } : cache;
}

/** その勤務に割り当てられている leg_run の id。leg_run 側のキャッシュを直す対象を決める */
export function collectLegRunIdsOfDelivery<T extends DeliveryLike>(
  cache: InfiniteDeliveriesCache<T> | undefined,
  shiftTruckId: number,
): number[] {
  if (!cache?.pages) return [];
  return cache.pages
    .flatMap((page) => page.data ?? [])
    .filter((delivery) => delivery.id === shiftTruckId)
    .flatMap((delivery) => delivery.shipmentOrderLegRuns ?? [])
    .map((legRun) => legRun.id)
    .filter((id): id is number => typeof id === 'number');
}

type LegRunListCache<T extends LegRunLike> = { pages: { data: T[] }[] };

/**
 * leg_run 側キャッシュの車番を揃える。
 *
 * /map は deliveries の compact な legRun を `/api/v5/shipment_order_leg_runs` の値で
 * 上書きして表示を組む（enrichedDeliveries）。こちらを直さないと、deliveries を空にしても
 * enrich で古い車番が戻り、空欄にした車番が画面に出ない。
 *
 * 一覧は取得時に 1 件ずつ `[KEY, { id }]` にも入る。prefix が同じで両方流れてくるので、
 * pages を持つかどうかで見分けて同じ関数で受ける。
 */
export function patchLegRunTruckNumber<C>(
  cache: C | undefined,
  legRunIds: ReadonlySet<number>,
  truckNumber: string,
): C | undefined {
  if (!cache || legRunIds.size === 0) return cache;
  const nextValue = truckNumber.trim().length > 0 ? truckNumber : null;

  const list = cache as unknown as LegRunListCache<LegRunLike>;
  if (Array.isArray(list.pages)) {
    let changed = false;
    const pages = list.pages.map((page) => {
      if (!page.data?.some((legRun) => legRun.id != null && legRunIds.has(legRun.id))) return page;
      changed = true;
      return {
        ...page,
        data: page.data.map((legRun) => (
          legRun.id != null && legRunIds.has(legRun.id) ? { ...legRun, truck_number: nextValue } : legRun
        )),
      };
    });
    return changed ? { ...cache, pages } : cache;
  }

  const single = cache as unknown as LegRunLike;
  if (single.id != null && legRunIds.has(single.id)) {
    return { ...single, truck_number: nextValue } as unknown as C;
  }
  return cache;
}
